import { useEffect, useRef, useState } from 'react';
import { Alert, Card, Space, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import { loadKshieldLegacyObservations } from './api';
import { LatestScopeRequest, type KshieldLegacyObservation, type KshieldLegacyDaily } from './lib';

const { Text } = Typography;
const show = (value: number | string | null) => value === null ? '—' : value;
const platforms = (value: KshieldLegacyDaily['appReportedAccountsByPlatform']) => value
  ? `APK ${value.apk}／Windows ${value.windows}／其他 ${value.other}／未知 ${value.unknown}` : '—';

export function KshieldLegacyObservations({ from, through, clearNames, refresh }: { from: string; through: string; clearNames: boolean; refresh: number }) {
  const [state, setState] = useState<{ key: string; data: KshieldLegacyObservation | null; error: string | null } | null>(null);
  const guard = useRef(new LatestScopeRequest());
  const key = `${from}:${through}:${clearNames ? 'clear' : 'masked'}:${refresh}`;
  useEffect(() => {
    const controller = new AbortController();
    const request = guard.current.begin(key);
    setState({ key, data: null, error: null });
    loadKshieldLegacyObservations(from, through, clearNames, controller.signal)
      .then(data => { if (guard.current.isCurrent(request, key)) setState({ key, data, error: null }); })
      .catch(error => { if (!controller.signal.aborted && guard.current.isCurrent(request, key)) setState({ key, data: null, error: error instanceof Error ? error.message : '讀取失敗' }); });
    return () => { controller.abort(); guard.current.invalidate(); };
  }, [key, from, through, clearNames]);
  const data = state?.key === key ? state.data : null;
  const error = state?.key === key ? state.error : null;
  const dailyColumns: TableProps<KshieldLegacyDaily>['columns'] = [
    { title: '臺北日期', dataIndex: 'date', fixed: 'left' },
    { title: 'App 回報帳號', render: (_, row) => show(row.appReportedAccounts) },
    { title: 'App 回報帳號平台', render: (_, row) => platforms(row.appReportedAccountsByPlatform) },
    { title: 'App 回報裝置', render: (_, row) => show(row.appReportedDevices) },
    { title: 'App 回報裝置平台', render: (_, row) => platforms(row.appReportedDevicesByPlatform) },
    { title: '收到的舊制帳號流量（bytes）', render: (_, row) => show(row.receivedBytes) },
    { title: '流量回報帳號', render: (_, row) => show(row.reportingAccounts) },
    { title: '來源筆數（連線／流量）', render: (_, row) => `${row.appConnectRows}／${row.bandwidthReportRows}` },
  ];
  return <Card title="K盾舊制觀測（獨立報表）" style={{ marginBottom: 16 }}>
    <p>這是已收到的 App 與舊制流量回報；涵蓋範圍未驗證，不代表實際 VPN 使用，也不計入上方的會員 Top 100。</p>
    <p>查詢截止：{data?.cutoffAt ? new Date(data.cutoffAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }) : '—'}（臺北時間）。當天顯示截至查詢時收到的資料。</p>
    {error && <Alert type="warning" showIcon message="K盾舊制觀測未提供" description={error} style={{ marginBottom: 12 }} />}
    <Space direction="vertical" style={{ width: '100%' }}>
      <Text>最近 15 分鐘 App 心跳帳號／裝置組合：{show(data?.recentAppHeartbeats.value ?? null)}。僅顯示已收到的 App 心跳。</Text>
      <Text type="secondary">連線回報：{data?.sources.appConnectReport.observedRows ?? '—'} 筆，最近 {data?.sources.appConnectReport.latestAt ? new Date(data.sources.appConnectReport.latestAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }) : '—'}；流量收件：{data?.sources.legacyBandwidthReceipt.observedRows ?? '—'} 筆，最近 {data?.sources.legacyBandwidthReceipt.latestAt ? new Date(data.sources.legacyBandwidthReceipt.latestAt).toLocaleString('zh-TW', { timeZone: 'Asia/Taipei' }) : '—'}；排除無效流量 {data?.sources.legacyBandwidthReceipt.excludedInvalidRows ?? '—'} 筆。</Text>
    </Space>
    <Table rowKey="date" columns={dailyColumns} dataSource={data?.daily ?? []} scroll={{ x: 1350 }} pagination={{ pageSize: 10 }} locale={{ emptyText: '—' }} style={{ marginTop: 12 }} />
    <Card size="small" title="收到的舊制帳號流量排行（非會員 Top 100）" style={{ marginTop: 16 }}>
      <p>期間有流量回報的帳號：{data?.ranking.periodReportingAccounts ?? '—'}；以下最多顯示 100 個帳號。僅依 BO 收到的舊制流量回報排序，未回報帳號不視為零。</p>
      <Table rowKey="rank" columns={[
        { title: '名次', dataIndex: 'rank' },
        { title: '舊制帳號', dataIndex: 'accountLabel' },
        { title: '收到的位元組', dataIndex: 'receivedBytes' },
        { title: '回報筆數', dataIndex: 'reportRows' },
      ]} dataSource={data?.ranking.rows ?? []} pagination={{ pageSize: 20 }} locale={{ emptyText: '—' }} />
    </Card>
  </Card>;
}
